import { ref, toValue } from 'vue';
import axios from 'axios';
import { searchOrganisations } from '@/modules/organisation/infrastructure/adapters/organisationService';
import { logger } from '@/infrastructure/services/LoggerService';
import { useAddressFormatter } from '@/shared/composables/useAddressFormatter';
function buildPrimaryAddress(org, formatter) {
    const primary = org.addresses.find((a) => a.isPrimary);
    if (!primary)
        return undefined;
    return formatter.format(primary) || undefined;
}
function toOption(org, extended, formatter) {
    return {
        value: org.id,
        label: org.mainInformation.legalName?.trim()
            || org.mainInformation.acronym?.trim()
            || org.id,
        logo: true,
        ...(extended ? {
            image: org.avatarUrl,
            description: buildPrimaryAddress(org, formatter),
        } : {}),
    };
}
/**
 * @param options.contactId When set, the search is restricted to the organisations this contact has
 *   an active relation with (OP-5603). Omitted ⇒ global legal-name search. Accepts a ref/getter so the
 *   value is re-read on each search.
 * @param options.extended When true, each option includes the organisation logo (image) and primary
 *   address (description) for richer dropdown display.
 */
export function useOrganisationSearch(options = {}) {
    const addressFormatter = useAddressFormatter();
    const suggestions = ref([]);
    const isLoading = ref(false);
    const isError = ref(false);
    let abortController = null;
    let requestId = 0;
    async function search(query) {
        abortController?.abort();
        if (!query.trim()) {
            suggestions.value = [];
            isLoading.value = false;
            isError.value = false;
            return;
        }
        abortController = new AbortController();
        const currentId = ++requestId;
        isLoading.value = true;
        isError.value = false;
        try {
            const response = await searchOrganisations({ search: query, pageSize: 10, contactId: toValue(options.contactId) }, abortController.signal);
            if (currentId !== requestId)
                return;
            suggestions.value = response.result.map((org) => toOption(org, options.extended ?? false, addressFormatter));
        }
        catch (err) {
            if (axios.isCancel(err))
                return;
            logger.error('Organisation search failed', err);
            suggestions.value = [];
            isError.value = true;
        }
        finally {
            if (currentId === requestId)
                isLoading.value = false;
        }
    }
    function clear() {
        abortController?.abort();
        suggestions.value = [];
        isError.value = false;
    }
    return { suggestions, isLoading, isError, search, clear };
}
